import { UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';

import {
  CreateGenericWebhookMutation,
  CreateGenericWebhookMutationVariables,
  CreateWorkvivoWebhookMutation,
  CreateWorkvivoWebhookMutationVariables,
  GenericStatus,
  GetWebhookListQuery,
  useGetWebhookListQuery,
  WebhookTypes,
} from 'api';
import { useNotify } from 'hooks';

type WorkvivoMutation = CreateWorkvivoWebhookMutation;
type WorkvivoMutationVariables = CreateWorkvivoWebhookMutationVariables;
type GenericMutation = CreateGenericWebhookMutation;
type GenericMutationVariables = CreateGenericWebhookMutationVariables;
type QueryData = GetWebhookListQuery;

export const useCreateWorkvivoWebhook = () => {
  const queryClient = useQueryClient();
  const queryKey = useGetWebhookListQuery.getKey();
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<WorkvivoMutation, unknown, WorkvivoMutationVariables> =
    {
      onMutate: async ({ workvivo_org_id, api_token }) => {
        await queryClient.cancelQueries(queryKey);

        const baseState = queryClient.getQueryData<QueryData>(queryKey);
        const nextState =
          baseState &&
          produce(baseState, draftState => {
            draftState.getWebhookList.webhooks.push({
              webhook_id: api_token,
              webhook_name: 'Workvivo',
              webhook_status: GenericStatus.Active,
              webhook_type: WebhookTypes.Workvivo,
              workvivo_org_id,
            });
          });

        if (baseState) {
          queryClient.setQueryData<QueryData>(queryKey, nextState);
        }

        return baseState;
      },
      onError: (error, mutationArgs, baseState: QueryData) => {
        if (baseState) {
          queryClient.setQueryData<QueryData>(queryKey, baseState);
        }
        notify.mutationError();
      },
      onSettled: () => {
        void queryClient.refetchQueries(queryKey);
      },
    };

  return mutationOptions;
};

export const useCreateGenericWebhook = () => {
  const queryClient = useQueryClient();
  const queryKey = useGetWebhookListQuery.getKey();
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<GenericMutation, unknown, GenericMutationVariables> = {
    onMutate: async ({ callback_url, secret_token, webhook_name }) => {
      await queryClient.cancelQueries(queryKey);

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.getWebhookList.webhooks.push({
            callback_url,
            webhook_id: secret_token,
            webhook_name,
            webhook_status: GenericStatus.Active,
            webhook_type: WebhookTypes.Generic,
          });
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: () => {
      void queryClient.refetchQueries(queryKey);
    },
  };

  return mutationOptions;
};
